import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Banner, Button, FieldLabel, FieldSwitch, FieldText, FieldWrapper, FlexBox, FormField, FormGrid, IconButton, Page, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Field, Form, reduxForm, } from 'redux-form';
import { analytics } from 'app/utils/segment';
import { addSubscription, getEvents, getSubscription, updateSubscription, } from 'components/settings/api/webhooks';
import { validateName } from 'utils/formValidator';
import { webhooksBasePath } from '../../webhooks.utils';
import { AuthorizationHeader } from '../authorizationHeader';
import { EventPicker } from './components/eventPicker';
import styles from './subscriptionForm.module.scss';
import { toCreatePayload, toFormValue, toUpdatePayload, validate, } from './subscriptionForm.utils';
function SubscriptionForm({ mode, initialize, handleSubmit, }) {
    const isEditMode = mode === 'edit';
    const [isCreating, setIsCreating] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [events, setEvents] = useState([]);
    const [error, setError] = useState();
    const navigate = useNavigate();
    const { id } = useParams();
    useEffect(() => {
        setError(null);
        getEvents()
            .then(({ data }) => setEvents(data.fqns))
            .catch(() => setError('Something went wrong while fetching the webhook events.'));
        if (id) {
            getSubscription(id)
                .then(({ data }) => initialize(toFormValue(data)))
                .catch(() => setError('Something went wrong while fetching the webhook.'));
        }
        else if (isEditMode) {
            setError('The id to fetch the subscription is missing.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleFormSubmit = useCallback((values) => {
        setError(null);
        if (isEditMode) {
            setIsUpdating(true);
            updateSubscription(toUpdatePayload(values))
                .then((response) => {
                if ('errors' in response) {
                    setError(response.errors[0]);
                }
                if ('data' in response) {
                    const { data } = response;
                    analytics.settingsIntegrationsWebhooksEditSave({
                        webhookId: data.id,
                    });
                    navigate(`${webhooksBasePath}/${data.id}`);
                }
                setIsUpdating(false);
            })
                .catch((error) => {
                setIsUpdating(false);
                setError(`Something went wrong while updating the webhook. ${error}`);
            });
        }
        else {
            setIsCreating(true);
            addSubscription(toCreatePayload(values))
                .then((response) => {
                if ('errors' in response) {
                    setError(response.errors[0]);
                }
                if ('data' in response) {
                    const { data } = response;
                    analytics.settingsIntegrationsWebhooksAddConfirm({
                        webhookId: data.id,
                    });
                    navigate(`${webhooksBasePath}/${data.id}`);
                }
                setIsCreating(false);
            })
                .catch((error) => {
                setIsCreating(false);
                setError(`Something went wrong while creating the webhook. ${error}`);
            });
        }
    }, [navigate, isEditMode]);
    return (_jsxs(Page.Section, Object.assign({ title: `${isEditMode ? 'Edit' : 'Create'} webhook`, titleMeta: _jsx(Tooltip, Object.assign({ content: "Help and documentation.", dark: true }, { children: _jsx(IconButton, { icon: "question", variant: "tertiary", onClick: () => window.open('https://docs.dixa.io/docs/webhooks-guide', '_blank', 'noopener,noreferrer') }) })), controls: _jsx(Button, Object.assign({ isLoading: isCreating || isUpdating, disabled: isEditMode ? !id : false, onClick: handleSubmit(handleFormSubmit) }, { children: "Save" })) }, { children: [error ? (_jsx(Text.Paragraph, { children: error })) : (_jsx(Form, Object.assign({ onSubmit: handleSubmit(handleFormSubmit) }, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "row", margin: "300 0 0 0" }, { children: [_jsx("div", Object.assign({ className: styles.details }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, { children: _jsx(Field, { label: "Name", name: "webhookName", component: FieldText, type: "text", placeholder: "Name", helpIcon: {
                                                text: 'Type a name here, so you can recognise your webhook easily later.',
                                            }, validate: validateName }) }), _jsx(FormField, { children: _jsx(Field, { label: "Webhook URL", name: "webhookUrl", component: FieldText, type: "text", placeholder: "Webhook URL", helpIcon: {
                                                text: 'Specify the URL that the events should be sent to. This could be an API from your internal system or e.g. a CRM like Salesforce.',
                                            } }) }), _jsx(FormField, { children: _jsx(FieldWrapper, Object.assign({ label: "Enable" }, { children: _jsx(Field, { name: "isEnabled", component: ({ input }) => (_jsx(FieldSwitch, { input: Object.assign(Object.assign({}, input), { checked: input.value }) })) }) })) }), _jsx(FormField, { children: _jsx(Field, { name: "headers", component: AuthorizationHeader }) })] }) })), _jsx("div", Object.assign({ className: styles.events }, { children: _jsx(FormGrid, { children: _jsxs(FormField, { children: [_jsx(FieldLabel, Object.assign({ className: styles.eventLabel, helpIcon: {
                                                text: 'Select the events you are interested in getting delivered to your systems.',
                                            } }, { children: "Events" })), _jsx(Field, { name: "subscriptionEvents", events: events, component: ({ input, meta, events }) => (_jsx(EventPicker, { events: events || [], input: input, meta: meta })) })] }) }) }))] })) }))), !isEditMode && !error && (_jsx(FlexBox, Object.assign({ margin: "200 0 0 0" }, { children: _jsx(Banner, { variant: "info", description: _jsx(_Fragment, { children: "By creating this webhook subscription, you instruct Dixa to transfer data to the organization owning the domain URL in the form below (input field 'Webhook URL'). Please note this may include personal data." }) }) })))] })));
}
const SubscriptionFormRedux = reduxForm({
    form: 'webhooksSubscriptionForm',
    validate,
})(SubscriptionForm);
export { SubscriptionFormRedux as SubscriptionForm };
